import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { ReportTemplateResponse } from "../../api-models/response/organization/ReportTemplateResponse";
import { ProjectFormatResponse } from "../../api-models/response/ProjectFormatResponse";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType, RowItemData } from "../../components/datatable/interfaces";
import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { translationService } from "../translation/TranslationService";
import { FileExtended } from "../upload/FileUploadTracker";
import { currentUserService } from "../user/CurrentUserStorage";
import { ReportTemplate } from "./ReportTemplate";

const ReportTemplatesModel = types.model({
  isUploadInProgress: false,
  remainingStorageSize: 0,
  remainingStorageSizeIsLoading: false,
  projectFormats: types.array(types.frozen<ProjectFormatResponse>()),
});

export const ReportTemplatesStorageModel = types
  .compose(DataGridStorageModel, ReportTemplatesModel)
  .named("ReportTemplatesStorage")
  .actions((self) => {
    return {
      getReportTemplateName(id: string) {
        if (self.data) {
          const items = self.data?.items as RowItemData[];
          return items.find((f) => f.id.toString() === id)?.displayValues.name.toDisplay;
        }
        return "";
      },

      downloadMultipleReportTemplates: flow(function* (reportTemplateIds: Array<number>) {
        const queryParams = serializeToQueryParams({
          reportTemplateIds: reportTemplateIds && reportTemplateIds.toString(),
        });
        const endpoint = endpoints.get("report_templates_download_zip");
        endpoint.queryParams = queryParams;

        self.deselectAll();

        yield getEnv(self).api.getDownloadToDisk(endpoint, "report_templates.zip");
      }),

      downloadSingleReportTemplate: flow(function* (reportTemplateId: number, reportTemplateName) {
        const endpoint = endpoints.get("report_template_download");
        endpoint.routeParams = [reportTemplateId];

        self.deselectAll();

        yield getEnv(self).api.getDownloadToDisk(endpoint, reportTemplateName);
      }),
    };
  })
  .views(() => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.report-templates.list-header.reportName",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "uploadedBy",
          name: "general.report-templates.list-header.uploadedBy",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "projectsCount",
          name: "general.report-templates.list-header.projectsCount",
          type: ColumnType.Number,
          sortable: true,
          filterable: true,
        },
        {
          key: "uploadedDate",
          name: "general.report-templates.list-header.dateUploaded",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "size",
          name: "general.report-templates.list-header.size",
          type: ColumnType.String,
          sortable: true,
        },
        {
          key: "lastEdit",
          name: "general.last-edit",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: ReportTemplateResponse) {
        return new ReportTemplate(item);
      },
      downloadDefaultTemplate: flow(function* (format: string) {
        try {
          const endpoint = endpoints.get("download_default_template");
          if (endpoint)
            endpoint.routeParams = [currentUserService.currentUser.currentOrganization.id, format];

          snackbarNotificationsService.showSuccess(
            translationService.translate("general.download.started")
          );
          yield getEnv(self).api.getDownloadToDisk(endpoint, `default-template-${format}.docx`);
        } catch (error) {
          snackbarNotificationsService.showError(
            translationService.translate("default-error.download")
          );
        }
      }),
      uploadReportTemplate: flow(function* (reportTemplateFile: FileExtended) {
        const endpoint = endpoints.get("upload_report_template");

        self.isUploadInProgress = true;
        yield getEnv(self).api.upload(endpoint, reportTemplateFile.getFileFormData());
        self.isUploadInProgress = false;

        yield self.load();
      }),
      deleteReportTemplates: flow(function* (reportTemplateFileIds: number[]) {
        const endpoint = endpoints.get("report_templates_delete");

        yield getEnv(self).api.delete(endpoint, reportTemplateFileIds);
        self.deselectAll();

        yield self.load();
      }),
      downloadReportTemplates: flow(function* () {
        try {
          snackbarNotificationsService.showSuccess(
            translationService.translate("general.download.started")
          );
          if (self.moreThanOneItemsSelected) {
            yield self.downloadMultipleReportTemplates([...self.selectedItems]);
          } else {
            const reportTemplateId = self.selectedItems[0];
            const reportTemplateName = self.getReportTemplateName(reportTemplateId);
            yield self.downloadSingleReportTemplate(reportTemplateId, reportTemplateName);
          }
        } catch (error) {
          snackbarNotificationsService.showError(
            translationService.translate("default-error.report-template-files.download")
          );
        }
      }),
      getRemainingStorage: flow(function* () {
        self.remainingStorageSizeIsLoading = true;
        const endpoint = endpoints.get("remaining_storage");
        const response = yield getEnv(self).api.get(endpoint);
        self.remainingStorageSize = response;
        self.remainingStorageSizeIsLoading = false;
      }),
      getProjectFormats: flow(function* () {
        const endpoint = endpoints.get("projects_formats");
        const response = yield getEnv(self).api.get(endpoint);
        self.projectFormats = response;
      }),
      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("get_report_templates_all");
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get areProjectFormatsLoading() {
      return self.projectFormats.length === 0;
    },
  }));

export type ReportTemplatesStorage = Instance<typeof ReportTemplatesStorageModel>;
